/**
 * Poll session.topic_timing while live so co-trainers stay aligned with primary trainer.
 */

import { useEffect, useRef } from "react";
import type { TopicTimingRecord } from "./topicProgress/types";
import {
  isAuthHttpError,
  isAuthSessionExpired,
} from "../utils/authSessionGuard";

export function mergeTopicTimingIfChanged(
  prev: TopicTimingRecord[],
  next: TopicTimingRecord[],
): TopicTimingRecord[] {
  if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
  return next;
}

export function useSessionTopicTimingPoll({
  sessionId,
  enabled,
  intervalMs = 30000,
  fetchRows,
  onTiming,
}: {
  sessionId: string;
  enabled: boolean;
  intervalMs?: number;
  fetchRows: () => Promise<TopicTimingRecord[]>;
  onTiming: (rows: TopicTimingRecord[]) => void;
}): void {
  const fetchRef = useRef(fetchRows);
  const onTimingRef = useRef(onTiming);
  fetchRef.current = fetchRows;
  onTimingRef.current = onTiming;

  useEffect(() => {
    if (!sessionId || !enabled) return;
    let cancelled = false;
    let intervalId: number | undefined;
    const stop = () => {
      cancelled = true;
      if (intervalId != null) {
        window.clearInterval(intervalId);
        intervalId = undefined;
      }
    };
    const poll = async () => {
      if (cancelled || isAuthSessionExpired()) {
        stop();
        return;
      }
      try {
        const rows = await fetchRef.current();
        if (!cancelled) onTimingRef.current(rows);
      } catch (err) {
        if (isAuthHttpError(err) || isAuthSessionExpired()) {
          stop();
        }
      }
    };
    void poll();
    intervalId = window.setInterval(() => void poll(), intervalMs);
    return () => stop();
  }, [sessionId, enabled, intervalMs]);
}
